import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AlertCircle, CheckCircle, Edit2, Eye, Loader2, Plus, Search, Trash2, Unlink, X
} from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import {
    AddKnowledgeSheet, DocumentDetailsDrawer, GenieKnowledgeCoachCard, KnowledgeBaseSmartPrompts,
    KnowledgeGeniusCard
} from '@/components/knowledge-base';
import {
    KnowledgeBrainOverview, KnowledgeGapsPanel, ReviewQueueCard, ReviewQueueDrawer,
    RewriteBrainDrawer
} from '@/components/knowledge-base/brain';
import {
    GenieAnswersPanel, GenieKnowledgeHeader, GenieKnowledgeTabs, KnowledgeHealthCounters
} from '@/components/knowledge-base/genie';
import type { KnowledgeHealthFilter } from '@/components/knowledge-base/genie';
import { BaseSheet } from '@/components/common/BaseSheet';
import { Button } from '@/components/ui/button';
import { useAgent } from '@/hooks/agent';
import { useDocuments } from '@/hooks/documents/useDocumentFetch';
import {
    useDeleteDocument, useDetachDocumentFromAgent, useRemoveDocumentFromKB,
    useSendDocumentToElevenLabs, useUpdateDocument
} from '@/hooks/documents/useDocumentMutations';
import { useSummariseDocuments } from '@/hooks/knowledge/useDocumentSummary';
import { useKnowledgeBrain } from '@/hooks/knowledge/useKnowledgeBrain';
import { useKnowledgeGaps } from '@/hooks/knowledge/useKnowledgeGaps';
import { cn } from '@/lib/utils';
import { DocumentService } from '@/services/api/DocumentService';
import { isGenieDocument } from '@/types/genie-documents/genie-document';
import { DocumentWithMetadata } from '@/types/knowledge/knowledge-base';
import type { KnowledgeGapQuestion, TopicCluster } from '@/types/knowledge/knowledge-brain';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

/**
 * Below this many sources the topic map has nothing to group and the plain-language
 * read would be noise, so the page drops straight to the library. Matches the
 * threshold the brain was originally designed against.
 */
const NEAR_EMPTY_THRESHOLD = 5;

const GenieKnowledgePage = () => {
  const { genieId } = useParams<{ genieId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: genie, isLoading: isLoadingGenie } = useAgent(genieId!);

  // If the genie belongs to a different user, switch to admin mode so we can
  // query documents owned by the genie's actual owner.
  const isOwnGenie = !genie?.user_id || genie.user_id === user?.id;
  const docViewMode = isOwnGenie ? "my-documents" : "all-documents";
  const docOwnerId = isOwnGenie ? undefined : genie?.user_id;

  // Fetch only documents for this genie using agentId filter
  const { data: knowledgeBaseDocuments = [], isLoading: isLoadingKnowledgeBase } = useDocuments(
    docViewMode,
    {
      agentId: genieId,
    },
    docOwnerId,
  );

  // Individual mutation hooks for cleaner API
  const deleteDocumentMutation = useDeleteDocument();
  const updateDocumentMutation = useUpdateDocument();
  const sendToElevenLabsMutation = useSendDocumentToElevenLabs();
  const removeFromKBMutation = useRemoveDocumentFromKB();
  const detachDocumentMutation = useDetachDocumentFromAgent();
  const {
    completed: summarisedSoFar,
    total: summarisingTotal,
    summariseAll,
  } = useSummariseDocuments();

  // Wrap mutations to match expected callback signatures
  const handleSendToElevenLabs = (doc: DocumentWithMetadata) => sendToElevenLabsMutation(doc);
  const handleRemoveFromKB = (doc: DocumentWithMetadata) => removeFromKBMutation(doc);

  const [selectedDocument, setSelectedDocument] = useState<DocumentWithMetadata | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentToDetach, setDocumentToDetach] = useState<DocumentWithMetadata | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentWithMetadata | null>(null);
  const [detachingDocId, setDetachingDocId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadSessionKey, setUploadSessionKey] = useState(0);
  const [uploadMethod, setUploadMethod] = useState<'pdf' | 'notes'>('pdf');
  const [uploadTitle, setUploadTitle] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [healthFilter, setHealthFilter] = useState<KnowledgeHealthFilter | null>(null);
  const [selectedCluster, setSelectedCluster] = useState<TopicCluster | null>(null);
  const [isRewriteOpen, setIsRewriteOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isPromptsOpen, setIsPromptsOpen] = useState(false);

  // Documents are already filtered by agentId on the server. Keep linked sources
  // visible while they are processing so the Genie's library is complete.
  //
  // Branded documents this Genie *produced* are attached to it too, and they are
  // deliberately excluded here: they are not sources. Leaving them in would let
  // the brain cluster the Genie's own summaries, count them toward coverage, and
  // offer them back as material for the next document.
  const genieDocuments = useMemo(
    () => knowledgeBaseDocuments.filter((document) => !isGenieDocument(document)),
    [knowledgeBaseDocuments],
  );

  const brandedDocumentCount = useMemo(
    () => knowledgeBaseDocuments.filter(isGenieDocument).length,
    [knowledgeBaseDocuments],
  );

  // ── What this Genie knows ──────────────────────────────────────────────────
  // Gaps come from this Genie's own calls, so the whole read is about one Genie
  // rather than averaged across the workspace. Gaps feed the brain, because an
  // unanswered question is what pulls a topic's confidence down.
  const {
    gaps,
    isLoading: isLoadingGaps,
    hasMore: hasMoreCalls,
    isScanningMore,
    scanMore,
  } = useKnowledgeGaps(genie ? [genie] : []);

  const openQuestions = useMemo(
    () => gaps.failedQuestions.map((question) => question.question),
    [gaps.failedQuestions],
  );

  const {
    clusters,
    summary,
    health,
    isLoading: isLoadingBrain,
    isPartial,
  } = useKnowledgeBrain({ userId: user?.id, documents: genieDocuments, openQuestions });

  // Calculate statistics (based on available metadata)
  const stats = useMemo(() => {
    const lastUpdated =
      genieDocuments.length > 0
        ? new Date(
            Math.max(
              ...genieDocuments.map((d) => new Date(d.updated_at || d.created_at || 0).getTime()),
            ),
          )
        : null;

    const connected = genieDocuments.filter(
      (document) => Boolean(document.el_doc_id) && document.status !== 'failed',
    ).length;

    return {
      totalDocuments: genieDocuments.length,
      lastUpdated,
      connected,
      notConnected: genieDocuments.length - connected,
    };
  }, [genieDocuments]);

  /**
   * Staleness is read off the same health report that feeds the review queue, so
   * the counter and the queue can never disagree about what "old" means.
   */
  const staleDocumentIds = useMemo(
    () =>
      new Set(
        health.all
          .filter((entry) => entry.signals.stale.state === 'flagged')
          .map((entry) => entry.documentId),
      ),
    [health.all],
  );

  const filteredDocuments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const clusterIds = selectedCluster ? new Set(selectedCluster.documentIds) : null;

    return genieDocuments.filter((document) => {
      const matchesSearch = !normalizedSearch || [
        document.name,
        document.metadata?.description,
        document.metadata?.summary,
        document.source_type,
        document.metadata?.type,
      ].some((value) => typeof value === 'string' && value.toLowerCase().includes(normalizedSearch));

      const isConnected = Boolean(document.el_doc_id) && document.status !== 'failed';
      const matchesHealth =
        healthFilter === null ||
        (healthFilter === 'connected' && isConnected) ||
        (healthFilter === 'not-connected' && !isConnected) ||
        (healthFilter === 'stale' && staleDocumentIds.has(document.id));

      const matchesCluster = !clusterIds || clusterIds.has(document.id);

      return matchesSearch && matchesHealth && matchesCluster;
    });
  }, [genieDocuments, searchTerm, healthFilter, staleDocumentIds, selectedCluster]);

  const hasActiveFilters = Boolean(searchTerm || healthFilter || selectedCluster);

  const clearFilters = () => {
    setSearchTerm('');
    setHealthFilter(null);
    setSelectedCluster(null);
  };

  const handleViewDocument = async (document: DocumentWithMetadata) => {
    if (document.source_path) {
      const signedUrl = await DocumentService.getSignedUrl(document.source_path);
      if (signedUrl) {
        window.open(signedUrl, "_blank");
      }
    }
  };

  const getDocumentTypeIcon = (document: DocumentWithMetadata) => {
    const docType = document.source_type || document.metadata?.type;

    const iconMap: Record<string, GenieIconName> = {
      audio_transcription: "microphone",
      video_transcription: "clapperboard",
      pdf_upload: "document",
      document_upload: "document",
      image_upload: "eye",
      website: "globe",
      "website-scrape": "globe",
      youtube: "clapperboard",
      notes: "chat",
      "quick-research": "brain",
      "deep-research": "brain",
      "application/pdf": "document",
      "text/plain": "document",
      "text/csv": "document",
    };

    return iconMap[docType || ""] ?? "document";
  };

  const getDocumentTypeLabel = (document: DocumentWithMetadata) => {
    const type = document.source_type || document.metadata?.type;
    const labels: Record<string, string> = {
      audio_transcription: 'Audio', video_transcription: 'Video', pdf_upload: 'PDF',
      document_upload: 'Document', image_upload: 'Image', website: 'Website',
      'website-scrape': 'Website', youtube: 'Video', notes: 'Notes',
      'quick-research': 'Research', 'deep-research': 'Research', 'application/pdf': 'PDF',
    };

    return labels[type || ''] || 'Document';
  };

  const openDocumentDetails = (doc: DocumentWithMetadata) => {
    setSelectedDocument(doc);
    setShowDocumentModal(true);
  };

  const closeDocumentDetails = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
  };

  const requestDocumentDeletion = (document: DocumentWithMetadata) => setDocumentToDelete(document);

  const handleDeleteDocument = (documentId: string) => {
    const document = genieDocuments.find((item) => item.id === documentId);
    if (document) setDocumentToDelete(document);
  };

  const confirmDeleteDocument = () => {
    if (!documentToDelete) return;

    deleteDocumentMutation.mutate(documentToDelete.id, {
      onSettled: () => setDocumentToDelete(null),
    });
  };

  const handleUpdateDocument = async (
    document: DocumentWithMetadata,
    updates: Partial<DocumentWithMetadata>,
  ) => {
    const updatedDoc = await updateDocumentMutation.mutateAsync({ document, updates });
    if (selectedDocument?.id === document.id && updatedDoc) {
      setSelectedDocument(updatedDoc);
    }
  };

  const handleRemoveFromGenie = (doc: DocumentWithMetadata) => {
    setDocumentToDetach(doc);
  };

  const confirmDetachDocument = () => {
    if (documentToDetach && documentToDetach.el_doc_id) {
      setDetachingDocId(documentToDetach.id);
      detachDocumentMutation.mutate(
        {
          agentId: genieId!,
          elDocIds: [documentToDetach.el_doc_id],
          documentIds: [documentToDetach.id],
        },
        {
          onSettled: () => {
            setDocumentToDetach(null);
            setDetachingDocId(null);
          },
        },
      );
    }
  };

  const openUpload = (method: 'pdf' | 'notes' = 'pdf', title?: string) => {
    setUploadMethod(method);
    setUploadTitle(title);
    setUploadSessionKey((current) => current + 1);
    setUploadOpen(true);
  };

  /**
   * Answering a gap means writing the answer, so this opens the notes editor
   * pre-titled with the caller's exact wording rather than a file picker.
   */
  const handleAnswerQuestion = (question: KnowledgeGapQuestion) => {
    openUpload('notes', question.question);
  };

  const handleReviewUpdateDocument = (document: DocumentWithMetadata) => {
    setIsReviewOpen(false);
    openDocumentDetails(document);
  };

  if (isLoadingGenie || isLoadingKnowledgeBase) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-genie-primary" />
          <p className="text-gray-600">Loading knowledge base...</p>
        </div>
      </div>
    );
  }

  if (!genie) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Genie Not Found</h2>
          <p className="text-gray-600 mb-4">
            The genie you're looking for doesn't exist or you don't have access to it.
          </p>
          <button
            onClick={() => navigate("/genie-hub/knowledge-base")}
            className="px-6 py-2 bg-genie-primary text-white rounded-lg hover:bg-genie-primary/90 transition-colors"
          >
            Back to Knowledge Base
          </button>
        </div>
      </div>
    );
  }

  const showBrain = genieDocuments.length >= NEAR_EMPTY_THRESHOLD;

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <DocumentDetailsDrawer
        document={selectedDocument}
        isOpen={showDocumentModal}
        onClose={closeDocumentDetails}
        onSendToKB={handleSendToElevenLabs}
        onRemoveFromKB={handleRemoveFromKB}
        onDocumentUpdate={handleUpdateDocument}
        onDocumentDelete={handleDeleteDocument}
      />

      <AddKnowledgeSheet
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        sessionKey={uploadSessionKey}
        initialMethod={uploadMethod}
        initialTitle={uploadTitle}
        defaultAgentId={genie.id}
        availableAgents={[
          { id: genie.id, elevenlabs_id: genie.elevenlabs_id, name: genie.name, branding: genie.branding },
        ]}
      />

      <RewriteBrainDrawer
        isOpen={isRewriteOpen}
        onClose={() => setIsRewriteOpen(false)}
        clusters={clusters}
        documents={genieDocuments}
        initialClusterId={selectedCluster?.id ?? null}
      />

      <ReviewQueueDrawer
        isOpen={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        queue={health.queue}
        allDocuments={genieDocuments}
        onUpdateDocument={handleReviewUpdateDocument}
      />

      <BaseSheet
        isOpen={isPromptsOpen}
        onClose={() => setIsPromptsOpen(false)}
        title="Ask a coverage question"
        description="Guided questions that check what this Genie can and cannot answer."
        size="lg"
      >
        <div className="px-6 sm:px-8 py-6">
          <KnowledgeBaseSmartPrompts variant="sheet" onPromptOpen={() => setIsPromptsOpen(false)} />
        </div>
      </BaseSheet>

      <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1400px]">
        <GenieKnowledgeHeader
          genie={genie}
          documentCount={stats.totalDocuments}
          lastUpdated={stats.lastUpdated}
          onBack={() => navigate("/genie-hub/knowledge-base")}
          onAddKnowledge={() => openUpload('pdf')}
        />

        <GenieKnowledgeTabs genieId={genie.id} documentCount={brandedDocumentCount} />

        {/* ── What this Genie knows ────────────────────────────────────────── */}
        {showBrain && (
          <KnowledgeBrainOverview
            clusters={clusters}
            summary={summary}
            isLoading={isLoadingBrain}
            isPartial={isPartial}
            selectedCluster={selectedCluster}
            onSelectCluster={setSelectedCluster}
            onRewrite={() => setIsRewriteOpen(true)}
            /* Documents are their own surface now — carry the selected topic
               across so the builder opens with those sources already ticked. */
            onCreateDocument={() =>
              navigate(
                `/genie-hub/knowledge-base/${genie.id}/documents?create=1${
                  selectedCluster ? `&cluster=${encodeURIComponent(selectedCluster.id)}` : ''
                }`,
              )
            }
          />
        )}

        {/* ── What your Genie noticed ──────────────────────────────────────
            Autonomous watcher output: website drift, unreachable pages, gaps.
            Scoped to this Genie's sources. */}
        <KnowledgeGeniusCard agentId={genie.id} />

        <KnowledgeHealthCounters
          counts={{
            connected: stats.connected,
            notConnected: stats.notConnected,
            stale: health.counts.stale,
            neverUsed: 0,
          }}
          active={healthFilter}
          onChange={setHealthFilter}
          /* Which documents the Genie actually cites needs per-answer logging that
             does not exist yet, so this tile says so rather than guessing. */
          neverUsedAvailable={false}
        />

        {/* ── What it can answer ───────────────────────────────────────────
            The readable layer. A filename tells you nothing; the questions a
            source can answer are what someone can scan in seconds. */}
        <GenieAnswersPanel
          documents={genieDocuments}
          onOpenDocument={openDocumentDetails}
          onSummariseAll={() => summariseAll(genieDocuments)}
          /* Zero when idle — the panel reads any positive count as "in flight",
             so a resting page must not report itself as mid-read. */
          summarisingCount={
            summarisingTotal ? Math.min(summarisedSoFar + 1, summarisingTotal) : 0
          }
          totalToSummarise={summarisingTotal}
        />

        {/* ── What needs a decision ────────────────────────────────────────── */}
        {showBrain && (
          <div className="mb-6 grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <KnowledgeGapsPanel
                gaps={gaps}
                isLoading={isLoadingGaps}
                hasMore={hasMoreCalls}
                isScanningMore={isScanningMore}
                onScanMore={scanMore}
                onAnswerQuestion={handleAnswerQuestion}
                onOpenPrompts={() => setIsPromptsOpen(true)}
              />
            </div>
            <ReviewQueueCard
              health={health}
              isLoading={isLoadingBrain}
              onStartReview={() => setIsReviewOpen(true)}
            />
          </div>
        )}

        {/* ── The library ──────────────────────────────────────────────────── */}
        {genieDocuments.length === 0 ? (
          <div className="bg-white p-12 rounded-xl border border-genie-border text-center">
            <GenieIcon
              name="files"
              size={80}
              color="#9CA3AF"
              className="mx-auto mb-6"
            />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No linked documents yet</h3>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              This Genie does not have any documents yet. Add knowledge to start building its library.
            </p>
            <button
              onClick={() => openUpload('pdf')}
              className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-genie-primary to-genie-secondary text-white rounded-lg hover:opacity-90 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add knowledge
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="border-b border-gray-200 p-4 md:p-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Document library</h2>
                  <p className="text-sm text-gray-600">Search, review, and manage the sources this Genie uses.</p>
                </div>
                <label className="relative min-w-0 sm:w-72">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search sources"
                    className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-genie-primary"
                  />
                </label>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <p className="text-xs text-gray-500">
                  Showing {filteredDocuments.length} of {stats.totalDocuments} sources
                </p>
                {selectedCluster && (
                  <button
                    type="button"
                    onClick={() => setSelectedCluster(null)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-genie-primary/30 bg-genie-primary-light px-3 py-1 text-xs font-semibold text-genie-primary transition-colors hover:bg-white"
                  >
                    Topic: {selectedCluster.label}
                    <X className="h-3 w-3" />
                  </button>
                )}
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs font-medium text-genie-primary hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            {filteredDocuments.length === 0 ? (
              <div className="px-5 py-12 text-center">
                <p className="font-medium text-gray-900">No sources match this view.</p>
                <button onClick={clearFilters} className="mt-2 text-sm text-genie-primary hover:underline">Clear filters</button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left">
                  <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-5 py-3">Source</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Last updated</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredDocuments.map((doc) => {
                      const needsReview = !doc.el_doc_id || doc.status === 'failed';
                      // `metadata.summary` is written by the document view; `description`
                      // is the older field and the one legacy rows carry.
                      const sourceSummary = doc.metadata?.summary || doc.metadata?.description;

                      return (
                        <tr
                          key={doc.id}
                          onClick={() => openDocumentDetails(doc)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openDocumentDetails(doc);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Open ${doc.name}`}
                          className="cursor-pointer transition-colors hover:bg-genie-primary/5 focus:bg-genie-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-genie-primary"
                        >
                          <td className="max-w-md px-5 py-4">
                            <div className="flex items-center gap-3">
                              <GenieIcon name={getDocumentTypeIcon(doc)} size={32} />
                              <div className="min-w-0">
                                <p className="truncate font-semibold text-gray-900">{doc.name}</p>
                                {sourceSummary ? (
                                  <p className="truncate text-xs text-gray-500">{sourceSummary}</p>
                                ) : (
                                  <span className="text-xs font-medium text-genie-primary">
                                    Not summarised yet — open to read it
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">{getDocumentTypeLabel(doc)}</td>
                          <td className="px-4 py-4">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium', needsReview ? 'bg-genie-secondary/10 text-genie-secondary' : 'bg-genie-primary/10 text-genie-primary')}>
                                {needsReview ? <AlertCircle className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
                                {needsReview ? 'Not connected' : 'In the Genie'}
                              </span>
                              {staleDocumentIds.has(doc.id) && (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                                  Over 6 months old
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {doc.updated_at || doc.created_at ? new Date(doc.updated_at || doc.created_at!).toLocaleDateString() : 'Not available'}
                          </td>
                          {/* The row itself opens the source, so this column carries only the
                              things the row click cannot do. Every control states what it does
                              in words — icon-only actions were unreadable. */}
                          <td className="px-5 py-4" onClick={(event) => event.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 gap-1.5 px-2.5 text-xs font-medium text-genie-primary hover:bg-genie-primary/10"
                                onClick={() => openDocumentDetails(doc)}
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                                Open
                              </Button>
                              {doc.source_path && (
                                <Button variant="ghost" size="icon" title="Open the original file in a new tab" className="h-8 w-8 text-gray-500 hover:bg-gray-100" onClick={() => handleViewDocument(doc)} aria-label={`Open the original file for ${doc.name}`}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" title="Remove from this Genie, keep in your library" disabled={detachingDocId === doc.id} className="h-8 w-8 text-genie-secondary hover:bg-genie-secondary/10 disabled:opacity-50" onClick={() => handleRemoveFromGenie(doc)} aria-label={`Remove ${doc.name} from this Genie`}>
                                {detachingDocId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
                              </Button>
                              <Button variant="ghost" size="icon" title="Delete this source permanently" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => requestDocumentDeletion(doc)} aria-label={`Delete ${doc.name}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Analysis belongs with the specific Genie's library, never above the full collection. */}
        <div className="mt-6">
          <GenieKnowledgeCoachCard genie={genie} documents={genieDocuments} />
        </div>
      </div>

      <BaseSheet
        isOpen={!!documentToDetach}
        onClose={() => !detachDocumentMutation.isPending && setDocumentToDetach(null)}
        title="Remove source from this Genie"
        description="This only removes the connection. The source remains in your knowledge base."
        size="md"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setDocumentToDetach(null)} disabled={detachingDocId !== null}>Cancel</Button>
            <Button onClick={confirmDetachDocument} disabled={detachingDocId !== null} className="bg-gradient-to-r from-genie-primary to-genie-secondary text-white">
              {detachingDocId ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Removing...</> : 'Remove from Genie'}
            </Button>
          </div>
        }
      >
        <div className="px-6 sm:px-8 py-6">
          <p className="text-sm text-gray-600">Remove <span className="font-semibold text-gray-900">{documentToDetach?.name}</span> from this Genie&apos;s library?</p>
        </div>
      </BaseSheet>

      <BaseSheet
        isOpen={!!documentToDelete}
        onClose={() => !deleteDocumentMutation.isPending && setDocumentToDelete(null)}
        title="Delete source"
        description="This permanently deletes the source from your knowledge base."
        size="md"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setDocumentToDelete(null)} disabled={deleteDocumentMutation.isPending}>Cancel</Button>
            <Button onClick={confirmDeleteDocument} disabled={deleteDocumentMutation.isPending} className="bg-gradient-to-r from-genie-primary to-genie-secondary text-white">
              {deleteDocumentMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : 'Delete source'}
            </Button>
          </div>
        }
      >
        <div className="px-6 sm:px-8 py-6">
          <p className="text-sm text-gray-600">Delete <span className="font-semibold text-gray-900">{documentToDelete?.name}</span>? This cannot be undone.</p>
        </div>
      </BaseSheet>
    </div>
  );
};

export default GenieKnowledgePage;
